/**
 * Tenant, country and vertical for a ClickUp task (decided 9 Oct 2026). Values come from, in order:
 *   1. space rules (CARFORCE is always Dubizzle, UAE; its vertical follows the list),
 *   2. ClickUp fields (Business = brand; ClickUp's "Tenant" field mostly holds countries; Vertical),
 *   3. [codes] in the task title, e.g. [Bayut KSA], [PK], [DZ EG], [H2E],
 *   3b. plain words in the title, e.g. "Bayut KSA share listings" (names in any case, short codes in capitals),
 *   4. the list name, e.g. "EG Business Requests",
 *   5. what a tenant implies (Zameen, MyZameen → Pakistan, Properties; Hatla2ee → Egypt, Motors; Bayut → Properties).
 * The first source that gives a dimension wins it. Nothing found: the dimension stays untagged.
 * A task can hold several values (e.g. KSA and PK); "All …" is its own value for work meant for every market.
 */
export const TENANTS = ['Bayut', 'Dubizzle', 'OLX', 'Zameen', 'MyZameen', 'Hatla2ee', 'LMS'];
export const COUNTRIES = ['Pakistan', 'Egypt', 'UAE', 'KSA', 'Oman', 'Bahrain', 'Qatar', 'Jordan', 'Kuwait'];
export const VERTICALS = [
    'Property',
    'Fractional Property',
    'Motors',
    'Goods',
    'Jobs',
    'Car Transactions',
    'VAS',
];
export const ALL_TENANTS = 'All tenants';
export const ALL_MARKETS = 'All markets';
export const ALL_VERTICALS = 'All verticals';
export const UNTAGGED = 'Untagged';
/**
 * Task types (12 Oct 2026), from ClickUp's own task type: Feature (Task, Feature, Epic, and User Story, Objective,
 * Flagship Feature, Milestone, Initiative), Bug (Bug), Maintenance (Internal, Request, QA, Support, Feedback).
 */
export const TYPES = ['Feature', 'Bug', 'Maintenance'];
const ITEM_TYPE = {
    task: 'Feature',
    feature: 'Feature',
    epic: 'Feature',
    'user story': 'Feature',
    objective: 'Feature',
    'flagship feature': 'Feature',
    milestone: 'Feature',
    initiative: 'Feature',
    bug: 'Bug',
    internal: 'Maintenance',
    request: 'Maintenance',
    qa: 'Maintenance',
    support: 'Maintenance',
    feedback: 'Maintenance',
};
/** The group for a ClickUp task type name, or null when it is not one we know. */
export const typeOfItem = (name) => name ? (ITEM_TYPE[name.trim().toLowerCase()] ?? null) : null;
export const TAG_DIMS = ['tenant', 'country', 'vertical', 'type'];
/** The market dimensions (tenant, country, vertical): what the "Untagged tasks" list asks teams to fix. */
export const MARKET_DIMS = ['tenant', 'country', 'vertical'];
export const TAG_LABEL = {
    tenant: 'Business',
    country: 'Country',
    vertical: 'Vertical',
    type: 'Type',
};
/** As used in the team's titles (9 Oct 2026): KSA, Oman, Bahrain, Qatar and Kuwait (not the UAE). */
const GCC = ['KSA', 'Oman', 'Bahrain', 'Qatar', 'Kuwait'];
const ALL_WORDS = new Set(['all', 'mena', 'all tenants', 'all tenant', 'global']);
/** Words (lower case) that name a tenant. */
const TENANT_WORDS = {
    bayut: 'Bayut',
    dubizzle: 'Dubizzle',
    dz: 'Dubizzle',
    olx: 'OLX',
    zameen: 'Zameen',
    zm: 'Zameen',
    z: 'Zameen',
    myzameen: 'MyZameen',
    mz: 'MyZameen',
    myz: 'MyZameen',
    hatla2ee: 'Hatla2ee',
    h2e: 'Hatla2ee',
    lms: 'LMS',
};
/** Words that name a country (or a group of them). */
const COUNTRY_WORDS = {
    pk: ['Pakistan'],
    pakistan: ['Pakistan'],
    eg: ['Egypt'],
    egypt: ['Egypt'],
    uae: ['UAE'],
    ae: ['UAE'],
    dubai: ['UAE'],
    ksa: ['KSA'],
    sa: ['KSA'],
    saudi: ['KSA'],
    om: ['Oman'],
    oman: ['Oman'],
    bh: ['Bahrain'],
    bahrain: ['Bahrain'],
    qa: ['Qatar'],
    qatar: ['Qatar'],
    jo: ['Jordan'],
    jordan: ['Jordan'],
    kw: ['Kuwait'],
    kuwait: ['Kuwait'],
    gcc: GCC,
};
/** Vertical names as typed in ClickUp. */
const VERTICAL_WORDS = {
    property: 'Property',
    properties: 'Property',
    'fractional property': 'Fractional Property',
    fractional: 'Fractional Property',
    re: 'Property',
    'real estate': 'Property',
    motors: 'Motors',
    motor: 'Motors',
    cars: 'Motors',
    goods: 'Goods',
    jobs: 'Jobs',
    'car transactions': 'Car Transactions',
    transactions: 'Car Transactions',
    'car services': 'VAS',
    vas: 'VAS',
};
const norm = (s) => s.toLowerCase().replace(/[‐-―]/g, '-').replace(/\s+/g, ' ').trim();
const uniq = (xs) => [...new Set(xs)];
/** "All …" stands alone: with real values next to it, the real ones win. */
const tidy = (xs, all) => {
    const u = uniq(xs);
    return u.length > 1 ? u.filter((x) => x !== all) : u;
};
/** Field values as a list of strings (ClickUp gives a dropdown name or a list of labels). */
function values(v) {
    if (v == null || v === '')
        return [];
    if (Array.isArray(v))
        return v.flatMap(values);
    return [String(v)];
}
/** Reads tenant and country words out of a phrase like "Bayut KSA", "dubizzle EG and OLX PK", "All tenants". */
function readPhrase(phrase) {
    const p = norm(phrase);
    const out = { tenant: [], country: [], all: ALL_WORDS.has(p) };
    for (const w of p.split(/[\s,/&+|]+|\band\b/).filter(Boolean)) {
        if (TENANT_WORDS[w])
            out.tenant.push(TENANT_WORDS[w]);
        if (COUNTRY_WORDS[w])
            out.country.push(...COUNTRY_WORDS[w]);
        if (w === 'all' || w === 'mena')
            out.all = true;
    }
    return out;
}
/**
 * Plain words in a title (outside [codes]). Names match in any case; short codes only as capitalised standalone
 * words, so "e.g." is not Egypt. QA is left out (quality assurance, not Qatar) and so is "jobs" (often "cron jobs").
 */
const TEXT_TENANT = [
    [/\bmy\s?zameen\b/i, 'MyZameen'],
    [/\bzameen\b/i, 'Zameen'],
    [/\bbayut\b/i, 'Bayut'],
    [/\bdubizzle\b/i, 'Dubizzle'],
    [/\bolx\b/i, 'OLX'],
    [/\bhatla2ee\b/i, 'Hatla2ee'],
    [/\b(?:DZ|Dz)\b/, 'Dubizzle'],
    [/\b(?:MZ|MyZ)\b/, 'MyZameen'],
    [/\bZM\b/, 'Zameen'],
    [/\bH2E\b/i, 'Hatla2ee'],
    [/\bLMS\b/, 'LMS'],
];
const TEXT_COUNTRY = [
    [/\bpakistan\b|\bPK\b/i, 'Pakistan'],
    [/\begypt\b|\bEG\b/, 'Egypt'],
    [/\begypt\b/i, 'Egypt'],
    [/\b(?:ksa|saudi)\b/i, 'KSA'],
    [/\b(?:uae|dubai|abu dhabi)\b/i, 'UAE'],
    [/\boman\b|\bOM\b/, 'Oman'],
    [/\boman\b/i, 'Oman'],
    [/\bbahrain\b/i, 'Bahrain'],
    [/\bBH\b/, 'Bahrain'],
    [/\bqatar\b/i, 'Qatar'],
    [/\bjordan\b/i, 'Jordan'],
    [/\bJO\b/, 'Jordan'],
    [/\bkuwait\b/i, 'Kuwait'],
    [/\bKW\b/, 'Kuwait'],
    [/\bgcc\b/i, 'GCC'],
];
const TEXT_VERTICAL = [
    [/\b(?:property|properties|real estate)\b/i, 'Property'],
    [/\b(?:motors?|cars)\b/i, 'Motors'],
    [/\bgoods\b/i, 'Goods'],
];
function readText(title) {
    const text = title.replace(/\[[^\]]*\]/g, ' ');
    const pick = (rules) => rules.filter(([re]) => re.test(text)).map(([, v]) => v);
    const country = pick(TEXT_COUNTRY).flatMap((c) => (c === 'GCC' ? GCC : [c]));
    // "MyZameen" also contains "Zameen": keep the more specific one.
    let tenant = pick(TEXT_TENANT);
    if (tenant.includes('MyZameen') && !/\bzameen\b/i.test(text.replace(/\bmy\s?zameen\b/gi, '')))
        tenant = tenant.filter((x) => x !== 'Zameen');
    return { tenant: uniq(tenant), country: uniq(country), vertical: uniq(pick(TEXT_VERTICAL)) };
}
/**
 * Fallback type when ClickUp's task type is missing or unknown: the Task Type, Sprint Category, Issue Type and Request
 * Category fields, then ClickUp tags, then the title (a marked type such as "… - Feature - …" before plain words).
 * Bugs are Bug; improvements, maintenance and support requests are Maintenance.
 */
const TYPE_FIELDS = ['Task Type', 'Sprint Category', 'Issue Type', 'Request Category'];
function typeWord(v) {
    const s = norm(v);
    if (/\bbug|\bissue|defect|hotfix|live issue/.test(s))
        return 'Bug';
    if (/support|data update|data request|access request/.test(s))
        return 'Support';
    if (/feature/.test(s))
        return 'Feature';
    if (/improve|enhance|maintenance|technical debt|tech debt|refactor/.test(s))
        return 'Improvement';
    return null;
}
const MARKED_TYPE = /(?:^|[-–|:\]]\s*|\[)(feature|new feature|bug|bugs|bug fix|hotfix|improvement|enhancement|data update|data request|support request|support)(?=\s*[-–|:\]])/i;
function titleType(title) {
    const m = MARKED_TYPE.exec(title);
    if (m)
        return typeWord(m[1]);
    const t = title.toLowerCase();
    if (/\bdata (update|request|correction)|\bsupport request|\baccess request/.test(t))
        return 'Support';
    if (/\b(bugs?|hotfix|fix(es|ed)?|broken|crash(es|ing)?|errors?|incorrect\w*|wrong\w*|fail(s|ed|ing|ure)?|glitch\w*|missing)\b|\bnot (working|showing|visible|updating|loading|saving|appearing|reflect\w*|receiv\w*|sen[dt]\w*)\b/.test(t))
        return 'Bug';
    if (/\b(improvements?|enhance(ment|ments)?|improve[ds]?|optimi[sz]\w*|refactor\w*|performance)\b/.test(t))
        return 'Improvement';
    if (/\b(features?|new|add(s|ed|ing)?|introduce\w*|enable\w*|implement\w*|build|launch\w*|integrat\w*)\b/.test(t))
        return 'Feature';
    return null;
}
export function classifyTask(t) {
    const r = { tenant: [], country: [], vertical: [], type: [], source: {} };
    // Type first: it does not depend on the space rule below.
    const typeFromFields = TYPE_FIELDS.flatMap((k) => values(t.fields[k]))
        .map(typeWord)
        .find((x) => !!x);
    const typeFromTags = (t.tags ?? []).map(typeWord).find((x) => !!x);
    const typeFromTitle = titleType(t.name);
    const three = (x) => [x === 'Feature' || x === 'Bug' ? x : 'Maintenance'];
    const fromItem = typeOfItem(t.itemType);
    if (fromItem)
        [r.type, r.source.type] = [[fromItem], 'item'];
    else if (typeFromFields)
        [r.type, r.source.type] = [three(typeFromFields), 'field'];
    else if (typeFromTags)
        [r.type, r.source.type] = [three(typeFromTags), 'field'];
    else if (typeFromTitle)
        [r.type, r.source.type] = [three(typeFromTitle), 'title'];
    const set = (dim, vals, src) => {
        if (r[dim].length || !vals.length)
            return;
        r[dim] = tidy(vals, dim === 'tenant' ? ALL_TENANTS : dim === 'country' ? ALL_MARKETS : ALL_VERTICALS);
        r.source[dim] = src;
    };
    const space = norm(t.space);
    const list = norm(t.list);
    const business = values(t.fields.Business).map(norm);
    const verticalField = values(t.fields.Vertical).map(norm);
    // 1. Space rules: CARFORCE is Dubizzle in the UAE; the list says Car Transactions or VAS.
    // CARFORCE also serves OLX in Pakistan (12 Oct 2026): a task that names OLX or Pakistan is OLX PK.
    if (space === 'carforce') {
        const said = readText(`${t.name} ${values(t.fields.Business).join(' ')} ${values(t.fields.Tenant).join(' ')}`);
        const olx = said.tenant.includes('OLX') || said.country.includes('Pakistan');
        set('tenant', [olx ? 'OLX' : 'Dubizzle'], 'rule');
        set('country', [olx ? 'Pakistan' : 'UAE'], 'rule');
        if (list.includes('car services'))
            set('vertical', ['VAS'], 'rule');
        else if (list.includes('transaction'))
            set('vertical', ['Car Transactions'], 'rule');
        else {
            const v = [...business, ...verticalField].map((x) => VERTICAL_WORDS[x]).filter((x) => !!x);
            set('vertical', v, 'field');
        }
        return r;
    }
    // 2. Fields. Business names the brand, sometimes with a country ("Bayut KSA"); ClickUp's "Tenant" field holds
    //    countries and sometimes brands.
    const bf = business.map((b) => readPhrase(TENANT_WORDS[b.replace(/\s+/g, '')] ? b.replace(/\s+/g, '') : b));
    set('tenant', bf.flatMap((x) => x.tenant), 'field');
    // ClickUp's "Entity" field names the brand too (Bayut, Dubizzle, Hatla2ee …; 15 Oct 2026).
    set('tenant', values(t.fields.Entity).flatMap((e) => readPhrase(e).tenant), 'field');
    const tf = values(t.fields.Tenant).map(readPhrase);
    set('tenant', tf.flatMap((x) => x.tenant), 'field');
    set('country', [...tf.flatMap((x) => x.country), ...(tf.some((x) => x.all) ? [ALL_MARKETS] : []), ...bf.flatMap((x) => x.country)], 'field');
    set('vertical', verticalField.map((v) => (ALL_WORDS.has(v) ? ALL_VERTICALS : VERTICAL_WORDS[v])).filter((x) => !!x), 'field');
    // 3. [Codes] in the title.
    const codes = [...t.name.matchAll(/\[([^\]]{1,40})\]/g)].map((m) => readPhrase(m[1]));
    set('tenant', codes.flatMap((x) => x.tenant), 'title');
    set('country', [...codes.flatMap((x) => x.country), ...(codes.some((x) => x.all) ? [ALL_MARKETS] : [])], 'title');
    // 3b. Plain words in the title ("Bayut KSA share listings", "Dubizzle Motors EG").
    const words = readText(t.name);
    set('tenant', words.tenant, 'title');
    set('country', words.country, 'title');
    set('vertical', words.vertical, 'title');
    // 4. The list name: a country prefix ("EG Business Requests", "KSA Release 61") and tenant names
    //    ("Zameen Business Request", "Bayut Business Request").
    const first = list.split(/[\s-]+/)[0] ?? '';
    if (COUNTRY_WORDS[first])
        set('country', COUNTRY_WORDS[first], 'list');
    set('tenant', readPhrase(list.replace(/\ball tenants?\b/g, '')).tenant, 'list');
    // 5. What a tenant implies.
    if (r.tenant.length === 1) {
        const tn = r.tenant[0];
        if (tn === 'Zameen' || tn === 'MyZameen') {
            set('country', ['Pakistan'], 'implied');
            set('vertical', [tn === 'MyZameen' ? 'Fractional Property' : 'Property'], 'implied');
        }
        else if (tn === 'Hatla2ee') {
            // Egypt only where the space has no other Hatla2ee country (Legion runs it in seven).
            const hc = (SPACE_TENANTS[spaceKey(t.space)] ?? []).filter(([b]) => b === 'Hatla2ee').map(([, c]) => c);
            if (new Set(hc).size <= 1)
                set('country', ['Egypt'], 'implied');
            set('vertical', ['Motors'], 'implied');
        }
        else if (tn === 'Bayut')
            set('vertical', ['Property'], 'implied');
    }
    // 6. STRAT's business rule from HR comes before the tenant list (its list has only OLX for Pakistan, but Pakistan
    //    property work is Zameen).
    if (!r.tenant.length && spaceKey(t.space) === 'strat') {
        const b = defaultBusiness(t.space, r.country, r.vertical);
        if (b.length)
            [r.tenant, r.source.tenant] = [b, 'rule'];
    }
    // 7. The space's tenants (HR's list): fill a missing business, country or vertical when only one fits.
    inferFromSpace(r, space);
    // 8. Still no business: the space's default business per country and vertical (16 Oct 2026).
    if (!r.tenant.length) {
        const b = defaultBusiness(space, r.country, r.vertical);
        if (b.length)
            [r.tenant, r.source.tenant] = [b, 'rule'];
    }
    // 9. Still no country: the space's default country for its business (16 Oct 2026).
    if (!r.country.length) {
        const c = defaultCountry(space, r.tenant);
        if (c.length)
            [r.country, r.source.country] = [c, 'rule'];
    }
    return r;
}
/**
 * The country a task is for when nothing on it names one, from its business (agreed with HR, 16 Oct 2026):
 * - PROPFORCE and Propone: Bayut is KSA; Zameen and MyZameen are Pakistan.
 */
export function defaultCountry(space, business) {
    const key = spaceKey(space);
    if (key !== 'propforce' && key !== 'propone')
        return [];
    const per = { Bayut: 'KSA', Zameen: 'Pakistan', MyZameen: 'Pakistan' };
    return uniq(business.map((b) => per[b]).filter((c) => !!c));
}
/**
 * The business a task belongs to when nothing on it names one, per space (agreed with HR, 16 Oct 2026):
 * - STRAT: Pakistan property is Zameen; any other Pakistan work (goods, motors, all verticals) is OLX; everything
 *   else is Dubizzle.
 * - LEGION: motors is Hatla2ee; Pakistan is Zameen (MyZameen for fractional property); otherwise Bayut.
 * - PROPFORCE and Propone: Pakistan is Zameen (MyZameen for fractional property); otherwise Bayut.
 * - JARVIS: Pakistan is OLX, KSA is Bayut, otherwise Dubizzle.
 * Each country gets its own business, so "KSA and PK" work in STRAT is Dubizzle KSA and OLX Pakistan.
 */
export function defaultBusiness(space, country, vertical) {
    const key = spaceKey(space);
    const has = (v) => vertical.includes(v);
    const property = has('Property') || has('Fractional Property');
    const zameen = has('Fractional Property') && !has('Property') ? 'MyZameen' : 'Zameen';
    const countries = country.filter((c) => c !== ALL_MARKETS);
    const per = (c) => {
        const pk = c === 'Pakistan';
        if (key === 'strat')
            return pk ? (property && !has(ALL_VERTICALS) ? 'Zameen' : 'OLX') : 'Dubizzle';
        if (key === 'legion')
            return has('Motors') ? 'Hatla2ee' : pk ? zameen : 'Bayut';
        if (key === 'propforce' || key === 'propone')
            return pk ? zameen : 'Bayut';
        if (key === 'jarvis')
            return pk ? 'OLX' : c === 'KSA' ? 'Bayut' : 'Dubizzle';
        return null;
    };
    return uniq((countries.length ? countries : [null]).map(per).filter((b) => !!b));
}
/**
 * Tenants per ClickUp space (HR's list, 12 Oct 2026): [business, country, verticals]. A tenant is a business in a
 * country ("Dubizzle · Egypt"). Used to fill in what a task leaves out when only one tenant fits, and to know which
 * business and country pairs make a tenant.
 */
const P = 'Property';
const M = 'Motors';
const G = 'Goods';
const FP = 'Fractional Property';
const PMG = [P, M, G];
export const SPACE_TENANTS = {
    jarvis: [
        ['Dubizzle', 'Egypt', PMG],
        ['Dubizzle', 'Oman', PMG],
        ['Dubizzle', 'Bahrain', PMG],
        ['Dubizzle', 'UAE', [M]],
        ['OLX', 'Pakistan', [G]],
        ['LMS', 'Egypt', [G]],
        ['Bayut', 'KSA', [P]],
    ],
    carforce: [
        ['Dubizzle', 'UAE', [M]],
        ['OLX', 'Pakistan', [M]],
    ],
    propforce: [
        ['Bayut', 'KSA', [P]],
        ['Zameen', 'Pakistan', [P]],
        ['MyZameen', 'Pakistan', [FP]],
        ['Bayut', 'UAE', [P]],
    ],
    propone: [
        ['Bayut', 'KSA', [P]],
        ['Zameen', 'Pakistan', [P]],
    ],
    legion: [
        ...['Bahrain', 'Egypt', 'KSA', 'Jordan', 'Oman', 'Qatar'].map((c) => ['Bayut', c, [P]]),
        ...['Egypt', 'KSA', 'UAE', 'Jordan', 'Qatar', 'Oman', 'Kuwait'].map((c) => [
            'Hatla2ee',
            c,
            [M],
        ]),
        ['Zameen', 'Pakistan', [P]],
        ['MyZameen', 'Pakistan', [FP]],
    ],
    strat: [
        ...['Egypt', 'Bahrain', 'Jordan', 'KSA', 'Kuwait', 'Oman', 'Qatar'].map((c) => [
            'Dubizzle',
            c,
            PMG,
        ]),
        ['Hatla2ee', 'Egypt', [M]],
        ['OLX', 'Pakistan', [G]],
    ],
};
/** Every business and country pair that is a tenant somewhere. */
const ALL_PAIRS = new Set(Object.values(SPACE_TENANTS).flatMap((xs) => xs.map(([b, c]) => `${b}|${c}`)));
const spaceKey = (space) => norm(space).replace(/[^a-z0-9]/g, '');
function inferFromSpace(r, space) {
    const rows = SPACE_TENANTS[spaceKey(space)];
    if (!rows)
        return;
    const real = (xs, all) => xs.filter((x) => x !== all);
    const bs = real(r.tenant, ALL_TENANTS);
    const cs = real(r.country, ALL_MARKETS);
    const fits = rows.filter(([b, c]) => (!bs.length || bs.includes(b)) && (!cs.length || cs.includes(c)));
    if (!bs.length && !cs.length)
        return;
    const one = (xs) => (new Set(xs).size === 1 ? [xs[0]] : []);
    if (!r.tenant.length) {
        const b = one(fits.map(([x]) => x));
        if (b.length)
            [r.tenant, r.source.tenant] = [b, 'space'];
    }
    if (!r.country.length) {
        const c = one(fits.map(([, x]) => x));
        if (c.length)
            [r.country, r.source.country] = [c, 'space'];
    }
    if (!r.vertical.length) {
        const v = one(fits.flatMap(([, , x]) => x));
        if (v.length)
            [r.vertical, r.source.vertical] = [v, 'space'];
    }
}
/** A tenant's name: the business and the country, e.g. "Dubizzle · Egypt". */
export const tenantName = (business, country) => `${business} · ${country}`;
/**
 * A task's tenants from its business and country tags: every pair that is a tenant (in HR's list), or "All" for
 * work meant for every tenant. None when either side is missing or no pair is a tenant.
 */
export function tenantsOf(business, country) {
    if (business.includes(ALL_TENANTS) || country.includes(ALL_MARKETS))
        return [ALL_TENANTS];
    const pairs = business.flatMap((b) => country.filter((c) => ALL_PAIRS.has(`${b}|${c}`)).map((c) => tenantName(b, c)));
    if (pairs.length)
        return pairs;
    // Half known (15 Oct 2026): ClickUp's "Tenant" field holds the country, so "EG" with no business read as
    // "Untagged" although the team had filled it in. Say what is missing instead.
    if (!business.length && country.length)
        return country.map((c) => tenantName(c, NO_BUSINESS));
    if (business.length && !country.length)
        return business.map((b) => tenantName(b, NO_COUNTRY));
    return [];
}
/** Labels for a tenant known only by its country or its business. */
export const NO_BUSINESS = 'business not set';
export const NO_COUNTRY = 'country not set';
/** The tenants HR lists for a space (by its name), or for every space when none is given, sorted by name. */
export function tenantsForSpace(space) {
    const rows = space ? (SPACE_TENANTS[spaceKey(space)] ?? []) : Object.values(SPACE_TENANTS).flat();
    return [...new Set(rows.map(([b, c]) => tenantName(b, c)))].sort((a, b) => a.localeCompare(b));
}
//# sourceMappingURL=tags.js.map